"use client";

import { useCallback, useMemo, type MouseEvent as ReactMouseEvent } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/frontend/ui/button";
import { cn } from "@/lib/utils";
import { useFavorites } from "@/contexts/frontend/favorites-context";
import { Relationship, resolveRelationshipId } from "@/lib/utils/theme-configuration-class";
import { trackDataLayerEvent } from "@/lib/data-measurements/datalayer";
import { Button as ButtonT } from "@/payload-types";
import { PropertyListingType } from "@/lib/types/marketsuite/datatypes/property";

export interface FavoriteToggleProps {
	propertyId: string;
	listingType: PropertyListingType;
	variant?: Relationship<ButtonT>;
	className?: string;
	ariaLabel?: string;
}

export function FavoriteToggle({ propertyId, listingType, variant, className, ariaLabel = "Toggle favorite" }: FavoriteToggleProps) {
	const favorites = useFavorites();
	const isFavorite = favorites?.has(propertyId, listingType) ?? false;

	const variantId = useMemo(() => resolveRelationshipId(variant), [variant]);

	if (!favorites) return null;

	const handleClick = useCallback(
		(e: ReactMouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();

			const action = isFavorite ? "removed" : "added";

			favorites.toggle(propertyId, listingType);
			trackDataLayerEvent("favorite_toggle", {
				action,
				property_id: propertyId,
				import_type: listingType,
				is_favorite: !isFavorite,
			});
		},
		[favorites, propertyId, listingType, isFavorite],
	);

	return (
		<Button
			type="button"
			variant={variantId}
			active={isFavorite}
			className={cn("max-w-max", className)}
			onClick={handleClick}
			aria-label={ariaLabel}
			aria-pressed={isFavorite}
		>
			<Heart className={cn("size-6 transition-colors", isFavorite ? "fill-(--ms-color-danger) text-(--ms-color-danger)" : "")} />
		</Button>
	);
}
